package com.example.hukakmp.viewmodel

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.example.hukakmp.auth.GoogleAuthProvider
import com.example.hukakmp.data.AuthRepository
import com.example.hukakmp.data.AuthState
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch

class LoginViewModel(
    private val authRepository: AuthRepository,
    private val googleAuthProvider: GoogleAuthProvider,
) : ViewModel() {

    data class LoginUiState(
        val isLoading: Boolean = false,
        val error: String? = null,
        val message: String? = null,
    )

    private val _authState = MutableStateFlow<AuthState>(AuthState.Loading)
    val authState: StateFlow<AuthState> = _authState.asStateFlow()

    private val _loginUiState = MutableStateFlow(LoginUiState())
    val loginUiState: StateFlow<LoginUiState> = _loginUiState.asStateFlow()

    init {
        viewModelScope.launch {
            authRepository.authState.collect { state ->
                _authState.value = state
                if (state is AuthState.Authenticated) {
                    _loginUiState.value = LoginUiState()
                }
            }
        }
    }

    fun signInWithEmail(email: String, password: String) {
        val validation = validateCredentials(email, password)
        if (validation != null) {
            _loginUiState.value = LoginUiState(error = validation)
            return
        }

        viewModelScope.launch {
            _loginUiState.value = LoginUiState(isLoading = true)
            when (val result = authRepository.signInWithEmailAndPassword(email, password)) {
                is AuthState.Authenticated -> {
                    _authState.value = result
                    _loginUiState.value = LoginUiState()
                }
                is AuthState.Error -> _loginUiState.value = LoginUiState(error = result.message)
                else -> _loginUiState.value = LoginUiState(error = "No se pudo iniciar sesión.")
            }
        }
    }

    fun createAccount(
        displayName: String,
        email: String,
        password: String,
        repeatedPassword: String,
    ) {
        when {
            displayName.trim().length < 2 -> {
                _loginUiState.value = LoginUiState(error = "Ingresá tu nombre.")
                return
            }
            password != repeatedPassword -> {
                _loginUiState.value = LoginUiState(error = "Las contraseñas no coinciden.")
                return
            }
        }
        val validation = validateCredentials(email, password)
        if (validation != null) {
            _loginUiState.value = LoginUiState(error = validation)
            return
        }

        viewModelScope.launch {
            _loginUiState.value = LoginUiState(isLoading = true)
            when (val result = authRepository.createUserWithEmailAndPassword(displayName, email, password)) {
                is AuthState.Authenticated -> {
                    _authState.value = result
                    _loginUiState.value = LoginUiState()
                }
                is AuthState.Error -> _loginUiState.value = LoginUiState(error = result.message)
                else -> _loginUiState.value = LoginUiState(error = "No se pudo crear la cuenta.")
            }
        }
    }

    fun resetPassword(email: String) {
        if (!looksLikeEmail(email)) {
            _loginUiState.value = LoginUiState(error = "Ingresá un correo válido para recuperar tu contraseña.")
            return
        }
        viewModelScope.launch {
            _loginUiState.value = LoginUiState(isLoading = true)
            val error = authRepository.sendPasswordResetEmail(email)
            _loginUiState.value = if (error == null) {
                LoginUiState(message = "Te enviamos un correo para restablecer tu contraseña.")
            } else {
                LoginUiState(error = error)
            }
        }
    }

    fun signInWithGoogle() {
        viewModelScope.launch {
            _loginUiState.value = LoginUiState(isLoading = true)
            val result = googleAuthProvider.signIn()
            val idToken = result.idToken
            if (idToken == null) {
                _loginUiState.value = LoginUiState(error = result.errorMessage ?: "No se pudo iniciar sesión con Google")
                return@launch
            }
            when (val authResult = authRepository.signInWithGoogleIdToken(idToken)) {
                is AuthState.Authenticated -> {
                    _authState.value = authResult
                    _loginUiState.value = LoginUiState()
                }
                is AuthState.Error -> _loginUiState.value = LoginUiState(error = authResult.message)
                else -> _loginUiState.value = LoginUiState(error = "No se pudo iniciar sesión con Google.")
            }
        }
    }

    fun clearFeedback() {
        _loginUiState.value = LoginUiState()
    }

    fun signOut() {
        viewModelScope.launch {
            authRepository.signOut()
        }
    }

    private fun validateCredentials(email: String, password: String): String? = when {
        !looksLikeEmail(email) -> "Ingresá un correo electrónico válido."
        password.length < 6 -> "La contraseña debe tener al menos 6 caracteres."
        else -> null
    }

    private fun looksLikeEmail(email: String): Boolean {
        val clean = email.trim()
        val at = clean.indexOf('@')
        return at > 0 && at < clean.lastIndex && '.' in clean.substring(at + 1)
    }
}
